import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import paymentMethodService from '../services/paymentMethodService';
import './ProfileIATAPage.css';

const formatDate = (val) => {
    const d = new Date(val);
    return isNaN(d) ? '—' : d.toLocaleDateString('en-GB');
};

const PaymentMethodsPage = () => {
    const navigate = useNavigate();
    const { ppId } = useParams();
    const [search, setSearch] = useState('');
    const [methods, setMethods] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => { loadMethods(); }, [ppId]); // eslint-disable-line

    const loadMethods = async () => {
        setLoading(true);
        try {
            const res = await paymentMethodService.list(ppId);
            setMethods(res.data?.methods || []);
        } catch (err) {
            console.error('Failed to load payment methods', err);
        } finally {
            setLoading(false);
        }
    };

    const filtered = methods.filter(m =>
        !search ||
        (m.code || '').toLowerCase().includes(search.toLowerCase()) ||
        (m.description || '').toLowerCase().includes(search.toLowerCase())
    );

    const handleDelete = async (id) => {
        if (!window.confirm('Delete this payment method?')) return;
        try {
            await paymentMethodService.remove(ppId, id);
            setMethods(prev => prev.filter(m => m.id !== id));
        } catch (err) {
            console.error('Failed to delete payment method', err);
        }
    };

    const MethodIcon = () => (
        <div className="pi-avatar" style={{ background: '#eff6ff' }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                <rect x="2" y="5" width="20" height="14" rx="2" stroke="#2563eb" strokeWidth="1.75"/>
                <path d="M2 10h20" stroke="#2563eb" strokeWidth="1.75"/>
                <path d="M6 15h4" stroke="#2563eb" strokeWidth="1.75" strokeLinecap="round"/>
            </svg>
        </div>
    );

    return (
        <div className="pi-page">
            <div className="pi-header">
                <div className="pi-header-left">
                    <button className="pi-back-btn" onClick={() => navigate(`/apps/payment-porter/${ppId}`)}>
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                            <path d="M10 3L5 8l5 5" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        Back to Payment Porter
                    </button>
                    <div className="pi-title-block">
                        <h1 className="pi-title">Payment Method Configuration</h1>
                        <p className="pi-subtitle">Configure payment method types for automated reservation filtering and processing</p>
                    </div>
                </div>
                <div className="pi-header-actions">
                    <button className="pi-btn-add" onClick={() => navigate(`/apps/payment-porter/${ppId}/payment-methods/new`)}>
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                            <path d="M8 3v10M3 8h10" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                        </svg>
                        Add Payment Method
                    </button>
                </div>
            </div>

            <div className="pi-search-wrap">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" className="pi-search-icon">
                    <circle cx="7" cy="7" r="4.5" stroke="#9ca3af" strokeWidth="1.5"/>
                    <path d="M11 11l3 3" stroke="#9ca3af" strokeWidth="1.5" strokeLinecap="round"/>
                </svg>
                <input
                    type="text"
                    className="pi-search-input"
                    placeholder="Search payment methods by code or description..."
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                />
            </div>

            <div className="pi-list-panel">
                {!loading && filtered.length === 0 ? (
                    <div className="pi-empty">
                        <div className="pi-empty-icon">
                            <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
                                <rect x="2" y="5" width="20" height="14" rx="2" stroke="#d1d5db" strokeWidth="1.5"/>
                                <path d="M2 10h20" stroke="#d1d5db" strokeWidth="1.5"/>
                                <path d="M6 15h4" stroke="#d1d5db" strokeWidth="1.5" strokeLinecap="round"/>
                            </svg>
                        </div>
                        <div className="pi-empty-title">
                            {search ? 'No payment methods match your search' : 'No payment methods configured'}
                        </div>
                        <div className="pi-empty-sub">
                            {search ? 'Try a different search term.' : 'Get started by adding your first payment method.'}
                        </div>
                    </div>
                ) : (
                    <table className="pi-table">
                        <thead>
                            <tr>
                                <th>Payment Method</th>
                                <th>Description</th>
                                <th>Status</th>
                                <th>Created</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.map(m => (
                                <tr key={m.id}>
                                    <td>
                                        <div className="pi-profile-cell">
                                            <MethodIcon />
                                            <span className="pi-id-cell">{m.code}</span>
                                        </div>
                                    </td>
                                    <td>{m.description || <span className="pi-no-value">—</span>}</td>
                                    <td>
                                        <span className={m.active ? 'pi-badge-active' : 'pi-badge-inactive'}>
                                            {m.active ? 'Active' : 'Inactive'}
                                        </span>
                                    </td>
                                    <td className="pi-location-cell">
                                        {m.created_at ? formatDate(m.created_at) : <span className="pi-no-value">—</span>}
                                    </td>
                                    <td>
                                        <div className="pi-actions">
                                            <button
                                                className="pi-btn-edit"
                                                onClick={() => navigate(`/apps/payment-porter/${ppId}/payment-methods/${m.id}/edit`)}
                                                title="Edit"
                                            >
                                                <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
                                                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                                    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                                </svg>
                                            </button>
                                            <button
                                                className="pi-btn-delete"
                                                onClick={() => handleDelete(m.id)}
                                                title="Delete"
                                            >
                                                <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
                                                    <polyline points="3 6 5 6 21 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                                    <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                                    <path d="M10 11v6M14 11v6" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                                                    <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                                </svg>
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
};

export default PaymentMethodsPage;
